import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { createStore } from 'vuex'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router/index'
import store from './stores/store'
import { preloadCriticalComponents } from './utils/asyncComponents'
import '@/styles/fonts.css'
import './style.css'

import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { vuetifyProTipTap } from './plugins/tiptap'
import { createPinia } from 'pinia'

// i18n with performance optimizations
const i18n = createI18n({
  legacy: false, // Vuetify does not support the legacy mode of vue-i18n
  locale: 'de',
  fallbackLocale: 'en',
  silentTranslationWarn: true, // Reduce console warnings in production
  silentFallbackWarn: true
})

// Optimize Vuex store - only include necessary functionality
const stores = createStore({
  modules: {
    store,
  },
  state: {
    dirty: false,
  },
  getters: {
    isDirty: (state) => state.dirty,
  },
  mutations: {
    setDirty(state) {
      state.dirty = true
    },
    unsetDirty(state) {
      state.dirty = false
    },
  },
  // Enable strict mode in development for better debugging
  // @ts-ignore
  strict: import.meta.env.DEV
})

// Optimized beforeunload handler
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (stores.getters.isDirty) {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
  })
}

// Initialize Pinia with performance optimizations
const pinia = createPinia()

// Add pinia plugin for better devtools integration in development
// @ts-ignore
if (import.meta.env.DEV) {
  pinia.use(({ store }) => {
    // Enable time travel debugging in development (only for relevant mutations)
    store.$subscribe((mutation, state) => {
      // Filter out frequent/unimportant mutations to reduce noise
      if (mutation.type !== 'direct') {
        const logData = {
          type: mutation.type,
          ...(('payload' in mutation) && { payload: mutation.payload })
        }
        console.debug(`Store [${store.$id}]:`, logData)
      }
    })
  })
}

// Create app with performance optimizations
const app = createApp(App)

// Install plugins in optimal order
app.use(pinia) // Pinia first for better store management
app.use(vuetify)
app.use(router)
app.use(i18n)
app.use(stores) // Keep Vuex for legacy compatibility
app.use(vuetifyProTipTap)

// Register global components
app.component('Datepicker', Datepicker)

// Global error handler for better debugging
app.config.errorHandler = (err, vm, info) => {
  console.error('Global error handler:', err, info)
  // In production, you might want to send errors to a logging service
}

// Performance configuration
// @ts-ignore
if (import.meta.env.PROD) {
  app.config.performance = false // Disable performance tracking in production
} else {
  app.config.performance = true // Enable performance tracking in development
}

// Initialize app with preloading
async function initApp() {
  try {
    // Preload critical components while router is getting ready
    const [routerReady] = await Promise.allSettled([
      router.isReady(),
      preloadCriticalComponents()
    ])

    if (routerReady.status === 'fulfilled') {
      app.mount('#app')

      // Performance mark for app initialization
      if (performance.mark) {
        performance.mark('app-mounted')
      }
    } else {
      console.error('Router initialization failed:', routerReady.reason)
      app.mount('#app') // Mount anyway
    }
  } catch (error) {
    console.error('App initialization error:', error)
    app.mount('#app') // Mount anyway with fallback
  }
}

// Initialize the app
initApp()
